import CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import findMonthItemRow from './findMonthItemRow';
import SurfaceDragHelperBase from './SurfaceDragHelperBase';
import TimeConstants from 'owa-datetime-utils/lib/TimeConstants';
import { addDays, DisplayDate } from 'owa-datetime';
import { addSurfaceBox } from '../../Templates';
import { CalendarMonthTransformProps } from '../../MonthSurfaceTransform';
import { createMonthSurfaceItem } from '../../transformers/generateMonthGrid';
import { DragDropHelperProps } from './DragDropHelperProps';
import { getAllowedResizeTypesForAllDayItem } from '../../store/calculation/getAllowedResizeTypes';
import { getWeekLayout } from '../../logic/getWeekLayout';
import { getWeeksInMonth } from 'owa-datetime-utils';
import { observer } from 'mobx-react';
import { trace } from 'owa-trace';
import {
    CalendarSurfaceStore,
    createSurfaceItemFromModel,
    DragOperationType,
    DroppableType,
    SurfaceItem,
} from 'owa-calendar-surface-dategrid';
import isItemBetweenDates from '../../store/calculation/isItemBetweenDates';
import getDisplayMonthRangeStart from '../../store/calculation/getDisplayMonthRangeStart';
import calculateWeekIndexFromDate from '../../store/calculation/calculateWeekIndexFromDate';

/**
 * Props for the month drag helper
 */
export interface MonthDragHelperProps extends DragDropHelperProps {
    allWeekItems: SurfaceItem[][][];
    config: CalendarMonthTransformProps;
}

/**
 * Month drag helper component
 */
@observer
export default class MonthDragHelper extends SurfaceDragHelperBase<MonthDragHelperProps, any> {
    private rowIndex: number = -1;

    protected get droppableType(): DroppableType {
        return DroppableType.Month;
    }

    protected onNotDragging = () => {
        this.rowIndex = -1;
    };

    protected getUnfoldedHelpers = () => {
        let { surfaceStore, config } = this.props;
        let { dragDropState } = surfaceStore;

        this.ensureRowIndex();

        return transformMonthItem(
            surfaceStore,
            config,
            dragDropState.helperItemModel,
            this.rowIndex
        );
    };

    private ensureRowIndex = () => {
        if (this.rowIndex < 0) {
            let { surfaceStore, allWeekItems } = this.props;
            let { dragDropState } = surfaceStore;
            let weekIndex = calculateWeekIndexFromDate(surfaceStore, dragDropState.dragStartDate);
            this.rowIndex = findMonthItemRow(
                dragDropState.draggedItemModel,
                weekIndex,
                allWeekItems
            );

            if (this.rowIndex < 0) {
                trace.warn(
                    'MonthDragHelper: Failed to find row index for dragged item during drag and drop, assuming index 0'
                );
                this.rowIndex = 0;
            }
        }
    };
}

/**
 * Transform a month item into one or more JSX elements
 * @param store The surface store
 * @param itemsConfigTable the table of configurations for items
 * @param item The surface item model
 * @param rowIndex The row index within the week for the item
 * @returns One or more JSX elements for each unfolded instance of the surface item
 */
function transformMonthItem(
    store: CalendarSurfaceStore,
    config: CalendarMonthTransformProps,
    item: CalendarEvent,
    rowIndex: number = 0
): JSX.Element[] {
    let date: DisplayDate = getDisplayMonthRangeStart(store);
    let from: DisplayDate = date;
    let to: DisplayDate = addDays(date, TimeConstants.DaysInOneWeek);
    let unfoldedItems: JSX.Element[] = [];

    // iterate over weeks to generate as many elements as needed for the item based on the item's dates
    for (
        let week = 0;
        week < getWeeksInMonth(store.dateRange.start, store.userOptions.weekStartDay);
        week++,
            from = addDays(from, TimeConstants.DaysInOneWeek),
            to = addDays(to, TimeConstants.DaysInOneWeek)
    ) {
        if (isItemBetweenDates(item, from, to)) {
            const weekColumnProvider = getWeekLayout(store, from);

            let unfoldedSurfaceItem = createSurfaceItemFromModel(
                item.ItemId.Id + '_monthDragHelper_' + week,
                item,
                weekColumnProvider
            );

            // During drag-and-drop there was a case where we would get no visible intervals
            // on the new week; it happened when dragging items across the DST-change week.
            if (unfoldedSurfaceItem) {
                let monthSurfaceItem = createMonthSurfaceItem(
                    store,
                    config.getItemConfig(item),
                    weekColumnProvider,
                    unfoldedSurfaceItem,
                    week,
                    rowIndex
                );

                var unfoldedGridDraggableItem = monthSurfaceItem;

                // If resizing, add drag drop grippers to the new item
                if (store.dragDropState.dragType != DragOperationType.Drag) {
                    let allowedResizeTypes = getAllowedResizeTypesForAllDayItem(
                        store,
                        config.dragDropConfig,
                        item
                    );
                    let colorScheme = config.dragDropConfig.colorProvider?.(item);
                    unfoldedGridDraggableItem = addSurfaceBox(
                        store,
                        unfoldedSurfaceItem,
                        monthSurfaceItem,
                        allowedResizeTypes,
                        colorScheme
                    );
                }

                unfoldedItems.push(unfoldedGridDraggableItem);
            }
        }
    }

    return unfoldedItems;
}
